import { Fragment, useMemo } from 'react';

const MONTHS = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);

function diffColor(diff) {
  if (diff > 0) return 'text-[#00C853]';
  if (diff < 0) return 'text-[#DC2626]';
  return 'text-[#6B7280]';
}

/**
 * "Previsões de Rede" tab — consultant forecasts from current month onwards,
 * grouped per consultor with summary row + monthly detail rows.
 */
export default function NetworkTab({ networkForecasts, consultoresRede, rf, setRf }) {
  const { curYear, curMonth, uniqConsultants, uniqYears, uniqMonths, consultants, filteredCount } = useMemo(() => {
    const today = new Date();
    const cy = today.getFullYear();
    const cm = today.getMonth() + 1;
    const base = networkForecasts.filter(f => (f.Ano > cy) || (f.Ano === cy && f.Mes >= cm));
    const uniqCons = (consultoresRede || [])
      .map(c => [c.ID_Cliente, c.NomeCompleto])
      .sort((a, b) => a[1].localeCompare(b[1], 'pt'));
    const uY = [...new Set(base.map(f => f.Ano))].sort();
    const uM = [...new Set(base.map(f => f.Mes))].sort((a, b) => a - b);

    const filtered = base.filter(f =>
      (!rf.consultor || String(f.ID_Cliente) === rf.consultor) &&
      (!rf.ano || String(f.Ano) === rf.ano) &&
      (!rf.mes || String(f.Mes) === rf.mes)
    );

    const groups = new Map();
    for (const f of filtered) {
      if (!groups.has(f.ID_Cliente)) groups.set(f.ID_Cliente, { id: f.ID_Cliente, name: f.NomeCompleto, items: [] });
      groups.get(f.ID_Cliente).items.push(f);
    }
    const sorted = [...groups.values()].sort((a, b) => a.name.localeCompare(b.name, 'pt'));
    sorted.forEach(g => g.items.sort((a, b) => (a.Ano - b.Ano) || (a.Mes - b.Mes)));

    return {
      curYear: cy, curMonth: cm,
      uniqConsultants: uniqCons, uniqYears: uY, uniqMonths: uM,
      consultants: sorted, filteredCount: filtered.length,
    };
  }, [networkForecasts, consultoresRede, rf]);

  const hasFilter = rf.consultor || rf.ano || rf.mes;
  const filterCls = "px-2.5 py-1.5 border border-[#D1D5DB] rounded-md text-xs bg-white focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] outline-none";

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB] flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-[#111827]">Previsões de Rede — {MONTHS[curMonth]} {curYear} em diante</h3>
        <div className="flex flex-wrap items-center gap-2">
          <select value={rf.consultor} onChange={e => setRf(p => ({ ...p, consultor: e.target.value }))} data-testid="rede-filter-consultor" className={`${filterCls} min-w-[160px]`}>
            <option value="">Todos os consultores</option>
            {uniqConsultants.map(([id, name]) => (<option key={id} value={id}>{name}</option>))}
          </select>
          <select value={rf.ano} onChange={e => setRf(p => ({ ...p, ano: e.target.value }))} data-testid="rede-filter-ano" className={filterCls}>
            <option value="">Todos os anos</option>
            {uniqYears.map(y => (<option key={y} value={y}>{y}</option>))}
          </select>
          <select value={rf.mes} onChange={e => setRf(p => ({ ...p, mes: e.target.value }))} data-testid="rede-filter-mes" className={filterCls}>
            <option value="">Todos os meses</option>
            {uniqMonths.map(m => (<option key={m} value={m}>{MONTHS[m]}</option>))}
          </select>
          {hasFilter && (
            <button onClick={() => setRf({ consultor: '', ano: '', mes: '' })} data-testid="rede-filter-clear" className="px-2.5 py-1.5 text-xs text-[#0F1829] hover:underline">
              Limpar
            </button>
          )}
          <span className="text-xs text-[#6B7280] ml-2">{consultants.length} consultores · {filteredCount} previsões</span>
        </div>
      </div>
      <div className="md:hidden px-4 py-2 text-[11px] text-[#9CA3AF] bg-[#F9FAFB] border-b border-[#E5E7EB]">
        ← Deslize horizontalmente para ver mais colunas →
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px] text-sm" data-testid="network-forecasts-table">
          <thead>
            <tr className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wide">
              <th className="px-4 py-2.5 text-left font-medium">Período</th>
              <th className="px-3 py-2.5 text-right font-medium">Pessoal (P)</th>
              <th className="px-3 py-2.5 text-right font-medium">Kits (P)</th>
              <th className="px-3 py-2.5 text-right font-medium">Clientes (P)</th>
              <th className="px-3 py-2.5 text-right font-medium" title="Nº de novos clientes diretos previstos">Diretos (P)</th>
              <th className="px-3 py-2.5 text-right font-semibold text-[#0F1829]">Total (P)</th>
              <th className="px-3 py-2.5 text-right font-medium">Pessoal (R)</th>
              <th className="px-3 py-2.5 text-right font-medium">Kits (R)</th>
              <th className="px-3 py-2.5 text-right font-medium">Clientes (R)</th>
              <th className="px-3 py-2.5 text-right font-medium" title="Nº de novos clientes diretos angariados">Diretos (R)</th>
              <th className="px-3 py-2.5 text-right font-semibold text-[#00C853]">Total (R)</th>
              <th className="px-3 py-2.5 text-right font-semibold text-[#111827]">Diferença</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {consultants.length === 0 && (
              <tr><td colSpan={12} className="px-4 py-8 text-center text-[#9CA3AF]">Sem previsões na rede para {MONTHS[curMonth]} {curYear} em diante.</td></tr>
            )}
            {consultants.map(g => {
              const sumP = g.items.reduce((s, f) => s + (f.VolumePessoal_P || 0) + (f.VolumeKits_P || 0) + (f.VolumeClientes_P || 0), 0);
              const sumR = g.items.reduce((s, f) => s + (f.VolumePessoal_R || 0) + (f.VolumeKits_R || 0) + (f.VolumeClientes_R || 0), 0);
              const sumDiretosP = g.items.reduce((s, f) => s + (f.NumDiretosNovos_P || 0), 0);
              const sumDiretosR = g.items.reduce((s, f) => s + (f.NumDiretosNovos_R || 0), 0);
              const sumDiff = sumR - sumP;
              return (
                <Fragment key={g.id}>
                  <tr className="bg-[#F3F4F6]">
                    <td colSpan={4} className="px-4 py-2 font-semibold text-[#111827] text-xs uppercase tracking-[0.08em]">
                      {g.name} <span className="text-[#6B7280] font-normal normal-case tracking-normal ml-2">({g.items.length} meses)</span>
                    </td>
                    <td className="px-3 py-2 text-right text-xs font-semibold text-[#374151]">{sumDiretosP || '-'}</td>
                    <td className="px-3 py-2 text-right text-xs font-semibold text-[#0F1829]">{fmt(sumP)}</td>
                    <td colSpan={3}></td>
                    <td className="px-3 py-2 text-right text-xs font-semibold text-[#374151]">{sumDiretosR || '-'}</td>
                    <td className="px-3 py-2 text-right text-xs font-semibold text-[#00C853]">{fmt(sumR)}</td>
                    <td className={`px-3 py-2 text-right text-xs font-semibold ${diffColor(sumDiff)}`}>{sumDiff > 0 ? '+' : ''}{fmt(sumDiff)}</td>
                  </tr>
                  {g.items.map(f => {
                    const totalP = (f.VolumePessoal_P || 0) + (f.VolumeKits_P || 0) + (f.VolumeClientes_P || 0);
                    const totalR = (f.VolumePessoal_R || 0) + (f.VolumeKits_R || 0) + (f.VolumeClientes_R || 0);
                    const diff = totalR - totalP;
                    const diretosP = f.NumDiretosNovos_P || 0;
                    const diretosR = f.NumDiretosNovos_R || 0;
                    const diretosDiff = diretosR - diretosP;
                    return (
                      <tr key={f.ID_Forecast} className="hover:bg-[#F9FAFB]">
                        <td className="px-4 py-2 pl-8 text-[#374151]">{MONTHS[f.Mes]} {f.Ano}</td>
                        <td className="px-3 py-2 text-right">{fmt(f.VolumePessoal_P)}</td>
                        <td className="px-3 py-2 text-right">{fmt(f.VolumeKits_P)}</td>
                        <td className="px-3 py-2 text-right">{fmt(f.VolumeClientes_P)}</td>
                        <td className="px-3 py-2 text-right text-[#374151] font-mono">{diretosP || '-'}</td>
                        <td className="px-3 py-2 text-right font-semibold text-[#0F1829]">{fmt(totalP)}</td>
                        <td className="px-3 py-2 text-right text-[#6B7280]">{fmt(f.VolumePessoal_R)}</td>
                        <td className="px-3 py-2 text-right text-[#6B7280]">{fmt(f.VolumeKits_R)}</td>
                        <td className="px-3 py-2 text-right text-[#6B7280]">{fmt(f.VolumeClientes_R)}</td>
                        <td className={`px-3 py-2 text-right font-mono ${diretosDiff >= 0 ? 'text-[#00C853]' : 'text-[#DC2626]'}`}>
                          {diretosR || '-'}{diretosP > 0 ? <span className="text-[10px] text-[#9CA3AF] ml-1">/{diretosP}</span> : null}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-[#00C853]">{fmt(totalR)}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${diffColor(diff)}`}>{diff > 0 ? '+' : ''}{fmt(diff)}</td>
                      </tr>
                    );
                  })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
